/**
 * The one map from a status to the colour it prints in. Every status label and every
 * status word in the app reads its tone from here, so a word prints in the
 * same colour on every surface.
 *
 * Tones, by meaning:
 * - `live`  needs the person now (a new repair, rent due today).
 * - `ok`    done and good (paid, signed, approved), status-ok green.
 * - `wait`  waiting on someone (pending, invited, scheduled), status-wait amber.
 * - `bad`   overdue, declined or failed, status-bad red.
 * - `blue`  a printed note (draft, vacancy).
 * - `ink`   the neutral record (unlisted, waived, closed).
 * `faint` marks a status that is over or off; it prints in mute ink.
 */

export const TONE_TEXT = {
  // live-deep: live itself is 3.4:1 on white, too light for small text.
  live: 'text-live-deep',
  ok: 'text-status-ok',
  wait: 'text-status-wait',
  bad: 'text-status-bad',
  blue: 'text-rule-blue',
  ink: 'text-ink',
}

const S = (label, tone, faint = false) => ({ label, tone, faint })

export const STATUS = {
  // Property
  vacancy: S('Vacancy', 'blue'),
  leased: S('Leased', 'ok'),
  awaiting_tenants: S('Awaiting tenants', 'wait'),
  pending_signatures: S('Pending signatures', 'wait'),
  unlisted: S('Unlisted', 'ink'),
  live: S('Live', 'ok'),
  inactive: S('Inactive', 'ink', true),

  // Rent and payments
  paid: S('Paid', 'ok'),
  completed: S('Completed', 'ok'),
  pending: S('Pending', 'wait'),
  processing: S('Processing', 'wait'),
  due: S('Due', 'live'),
  overdue: S('Overdue', 'bad'),
  late: S('Late', 'bad'),
  partial: S('Partial', 'wait'),
  failed: S('Failed', 'bad'),
  recorded: S('Recorded', 'ok'),
  not_yet: S('Not yet', 'wait'),
  waived: S('Waived', 'ink'),
  upcoming: S('Upcoming', 'ink'),
  scheduled_payment: S('Scheduled', 'wait'),
  // Household bills: a roommate's share still owed back, a bill all paid.
  unpaid: S('Unpaid', 'wait'),
  settled: S('Settled', 'ok'),
  // Payment automation: a tenant's "I paid" waiting for the bank record, a
  // bank line recorded against what was owed, a likely match waiting for
  // the landlord, money in nobody owes yet, a recorded payment taken back.
  claimed: S('Claimed', 'wait'),
  matched: S('Matched', 'ok'),
  needs_review: S('Needs review', 'live'),
  unassigned: S('Unassigned', 'wait'),
  voided: S('Voided', 'ink', true),
  ignored: S('Ignored', 'ink', true),

  // Lease
  signed: S('Signed', 'ok'),
  sealed: S('Sealed', 'ok'),
  not_sealed: S('Signed, no seal', 'wait'),
  awaiting_signatures: S('Awaiting signatures', 'wait'),
  awaiting_confirmations: S('Awaiting confirmations', 'wait'),
  invited: S('Invited', 'wait'),
  not_signed: S('Not signed', 'wait'),
  confirmed: S('Confirmed', 'ok'),
  active: S('Active', 'ok'),
  ended: S('Ended', 'ink', true),
  expired: S('Expired', 'ink', true),
  terminated: S('Terminated', 'ink', true),
  draft: S('Draft', 'blue'),
  signed_off: S('Signed off Rentra', 'ink', true),
  signature_needed: S('Your signature needed', 'live'),
  ending: S('Ending', 'live'),

  // Application
  approved: S('Approved', 'ok'),
  declined: S('Declined', 'bad'),
  rejected: S('Declined', 'bad'),
  not_selected: S('Not selected', 'bad'),
  withdrawn: S('Withdrawn', 'ink', true),
  cancelled: S('Cancelled', 'ink', true),
  pre_qualified: S('Pre-qualified', 'ink'),
  // Landlord pipeline stages
  verifying: S('Verifying', 'wait'),
  ready: S('Ready', 'live'),
  lease_sent: S('Lease sent', 'wait'),
  signing: S('Signing', 'wait'),
  executed: S('Executed', 'ok'),

  // Maintenance ticket
  received: S('Received', 'live'),
  acknowledged: S('Acknowledged', 'wait'),
  scheduled: S('Scheduled', 'wait'),
  vendor_done: S('Vendor reported done', 'wait'),
  verified: S('Verified', 'ok'),
  closed: S('Closed', 'ink', true),

  // Deposit
  holding: S('Holding', 'wait'),
  returned: S('Returned', 'ok'),
  // A tenant's share is covered by the deposit amount on record for the
  // household (deposits that predate the per-tenant ledger).
  on_record: S('On record', 'ok'),
  not_requested: S('No share', 'ink', true),
  // A bank transfer of deposit money (DepositTransfer.status).
  canceled: S('Canceled', 'ink', true),

  // Automation
  applied: S('Applied', 'ok'),
  sent: S('Sent', 'ok'),
  skipped: S('Skipped', 'ink'),
  dismissed: S('Dismissed', 'ink', true),
  clear: S('Clear', 'ink'),

  // Showings
  requested: S('Requested', 'wait'),
  booked: S('Booked', 'ok'),
  no_show: S('No-show', 'bad'),

  // Invites, accounts and checks
  accepted: S('Accepted', 'ok'),
  // The only account check Rentra runs today: never print "Verified" for it.
  email_confirmed: S('Email confirmed', 'ok'),
  // A housemate thread where the other person wrote last, over a day ago.
  your_turn: S('Your turn', 'live'),
  connected: S('Connected', 'ok'),
  updated: S('Updated', 'ok'),
  shared: S('Shared', 'ok'),
  saved: S('Saved', 'ink'),
  complete: S('Complete', 'ok'),
  incomplete: S('Incomplete', 'wait'),
  not_started: S('Not started', 'ink'),
  // Settings, not outcomes: ink, so they also print on a Carbon sheet.
  visible: S('Visible', 'ink'),
  paused: S('Paused', 'ink'),
  on: S('On', 'ink'),
}

/** "Awaiting tenants" / "awaiting-tenants" / "AWAITING_TENANTS" → "awaiting_tenants". */
export function statusKey(value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

/** The entry for a status key or printed word, or null when unknown. */
export function statusEntry(value) {
  const key = statusKey(value)
  if (!key) return null
  if (STATUS[key]) return STATUS[key]
  // A printed word with a date or name after it ("Signed Oct 3").
  const first = key.split('_')[0]
  return STATUS[first] || null
}

/**
 * `{ label, tone, faint, className }` for a status. `label` overrides the
 * printed word (the tone still comes from the status). Unknown statuses
 * print in ink.
 */
export function statusMeta(status, label) {
  const entry = statusEntry(status) || statusEntry(label)
  const tone = entry ? entry.tone : 'ink'
  const faint = entry ? entry.faint : false
  return {
    status: statusKey(status),
    label: label ?? (entry ? entry.label : String(status ?? '')),
    tone,
    faint,
    // As plain text a faint status prints in mute ink, which passes AA.
    className: faint ? 'text-ink-mute' : TONE_TEXT[tone],
  }
}

/** The tone name for a status ("pending" → "wait"). */
export const statusTone = status => statusMeta(status).tone
